import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSyncedPreference } from '@/hooks/useSyncedPreference';
import { TRADER_COLUMNS } from '@/lib/traderColumns';

/****************
 * THE LEADERBOARD'S FILTERS, DEFINED ONCE. 2026-10-06.
 *
 * Guillaume, on the asset page's "Traders on this asset": "maybe add the same
 * filter criteria as the traders leader board on the dashboard". Same
 * criteria means the same code: the min/max on every column, the rule that a
 * trader with no value fails a bound, and the saved combinations, which are
 * the user's own and the same list on both tables. Two copies would drift the
 * first time one was touched (the reason the three charts became one file).
 ****************/

export type ColFilters = Record<string, { min?: string; max?: string }>;
export type FilterPreset = { name: string; filters: ColFilters };

const set = (v?: string) => v !== undefined && v !== '';

/** How many columns carry a bound. */
export function colFilterCount(f: ColFilters): number {
  return Object.values(f).filter((x) => set(x?.min) || set(x?.max)).length;
}

/** Does a trader row pass every bound? */
export function passesColFilters(t: any, f: ColFilters): boolean {
  return TRADER_COLUMNS.every((col) => {
    const b = f[col.key];
    if (!b || (!set(b.min) && !set(b.max))) return true;
    const v = col.get(t);
    // A trader with no value for the metric fails any bound on it. Letting
    // them through would mean "traders who are at least 60% long" quietly
    // included traders whose direction we do not know.
    if (v === null || v === undefined || Number.isNaN(v)) return false;
    if (set(b.min) && !(v >= parseFloat(b.min as string))) return false;
    if (set(b.max) && !(v <= parseFloat(b.max as string))) return false;
    return true;
  });
}

const MAX_PRESETS = 20;

/**
 * Saved combinations and the min/max inputs. Stored per USER (the
 * preferences store), so a preset made on the web is there on the phone and
 * on every table that uses this panel. Twenty at most, far under the
 * server's 16 KB limit for one preference.
 */
export function TraderMetricFilters({
  value,
  onChange,
  otherActive = 0,
}: {
  value: ColFilters;
  onChange: (f: ColFilters) => void;
  /** Filters set outside this panel (asset type, broker), for "set some first". */
  otherActive?: number;
}) {
  const { t } = useTranslation('dashboard');
  const [presets, setPresets] = useSyncedPreference<FilterPreset[]>('copyfi.topTraders.filterPresets', []);
  const [name, setName] = useState('');
  const active = colFilterCount(value) + otherActive;
  const save = () => {
    const n = name.trim();
    if (!n || active === 0) return;
    const kept = Object.fromEntries(Object.entries(value).filter(([, v]) => set(v?.min) || set(v?.max)));
    setPresets([{ name: n, filters: kept }, ...presets.filter((p) => p.name !== n)].slice(0, MAX_PRESETS));
    setName('');
  };
  const bound = (key: string, which: 'min' | 'max', v: string) =>
    onChange({ ...value, [key]: { ...value[key], [which]: v } });

  return (
    <>
      {/* Saved combinations: one click to apply, x to forget. */}
      <div className="mb-3 space-y-2">
        {presets.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {presets.map((preset) => (
              <span key={preset.name} className="inline-flex items-center rounded-full border border-border/70 bg-muted/40 text-[11px]">
                <button type="button" className="px-2 py-0.5 hover:text-primary" onClick={() => onChange(preset.filters)} title={t('filters.applyPreset')}>
                  {preset.name}
                </button>
                <button
                  type="button"
                  className="pe-2 text-muted-foreground hover:text-destructive"
                  onClick={() => setPresets(presets.filter((p) => p.name !== preset.name))}
                  aria-label={t('filters.deletePreset', { name: preset.name })}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Input
            placeholder={active > 0 ? t('filters.nameThem') : t('filters.setThenName')}
            className="h-7 text-xs flex-1"
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          />
          <Button size="sm" className="h-7 px-2 text-[11px]" onClick={save} disabled={active === 0 || name.trim() === ''}>
            {t('filters.save')}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {TRADER_COLUMNS.map((col) => (
          <div key={col.key} className="flex items-center gap-2">
            {/* Round 11 #510: the whole label, on two lines if it must, never cut with "…". */}
            <span className="text-xs text-muted-foreground flex-1 leading-tight break-words" title={t(col.label)}>
              {t(col.label)}{col.unit ? ` (${col.unit})` : ''}
            </span>
            <Input
              type="number" placeholder={t('filters.min')} className="h-7 w-20 text-xs"
              value={value[col.key]?.min ?? ''}
              onChange={(e) => bound(col.key, 'min', e.target.value)}
            />
            <Input
              type="number" placeholder={t('filters.max')} className="h-7 w-20 text-xs"
              value={value[col.key]?.max ?? ''}
              onChange={(e) => bound(col.key, 'max', e.target.value)}
            />
          </div>
        ))}
      </div>
    </>
  );
}
